import { attachmentField, bounds, checkbox, enumSelectOptions, forbiddenIf, form, grid, integerField, numberField, page, pointField, required, requiredIf, requiredIff, section, select, textArea, textField, timestampField, } from "@cartographerio/topo-form";
import { MrsArtificialGroundCoverEnum, MrsAtpeEnum, MrsMajorIntermediateMinorEnum, MrsMorphEstuariesCreekTypeEnum, MrsMorphEstuariesDaysToSpringTideEnum, MrsMorphEstuariesDrainageNetworkTypeEnum, MrsMorphEstuariesFloodWallDimensionEnum, MrsMorphEstuariesFragmentationEnum, MrsMorphEstuariesFrontBackCreekMarginEnum, MrsMorphEstuariesGrazingEnum, MrsMorphEstuariesMarginTypeEnum, MrsMorphEstuariesSaltMarshLocationEnum, MrsMorphEstuariesSmallFragmentDistributionEnum, MrsMorphEstuariesSubtidalSedimentSizeEnum, MrsMorphEstuariesTidalFloodBarrierEnum, MrsMorphEstuariesVegetatedIntertidalSedimentSizeEnum, MrsMorphEstuariesVisibilityEnum, MrsReinforcementTypeEnum, MrsReinforcementVerticalExtentEnum, MrsTreeTypeEnum, UrsSurveyBankEnum, UrsSurveyTypeEnum, } from "@cartographerio/inventory-enums";
import { outdent } from "outdent";
import { morphApeSelect, morphAtpeSelect, morphBanks, morphChannel, morphCount, morphSection, morphSelect, morphText, } from "../MrsMorph/formHelpers.js";
function photographsAndNotes(path = ["data", "surveyDetails"]) {
    return section({
        title: "Photographs and Notes",
        path: path,
        blocks: [
            attachmentField({
                label: "Photographs",
                path: ["photographs"],
                fullWidth: true,
                maxFiles: 4,
            }),
            textArea({
                label: "Notes",
                path: ["notes"],
                fullWidth: true,
                rows: 8,
            }),
        ],
    });
}
export function mrsMorphEstuariesForm(title) {
    return form({
        title,
        pages: [
            page({
                title: "General Information",
                path: [],
                blocks: [
                    section({
                        title: "Project Details",
                        path: [],
                        blocks: [
                            textField({
                                label: "Project Name",
                                path: ["data", "surveyDetails", "projectName"],
                                help: outdent `
                Name of the river restoration project being monitored (if applicable).
                `,
                            }),
                            textField({
                                label: "Project Code",
                                path: ["data", "surveyDetails", "projectCode"],
                                help: outdent `
                Code for the restoration project being monitored (if applicable).
                `,
                            }),
                            textField({
                                label: "WFD Waterbody ID",
                                path: ["data", "surveyDetails", "wfdWaterBodyId"],
                                help: outdent `
                ID used to identify the water body for the purposes of
                Water Framework Directive reporting.
                `,
                            }),
                            select({
                                label: "Survey Type",
                                path: ["data", "surveyDetails", "surveyType"],
                                options: enumSelectOptions(UrsSurveyTypeEnum),
                                help: outdent `
                Is this survey for general *monitoring* purposes,
                is it a *pre-project, post-project or post-recovery assessment*,
                is it testing a hypothetical *scenario*,
                or is it a dummy survey created during a *training* course?
                `,
                            }),
                            textField({
                                label: "Scenario Name",
                                path: ["data", "surveyDetails", "scenarioName"],
                                help: outdent `
                If you selected *scenario* above, enter a scenario name here.
                The name will be searchable on the survey list.
                `,
                                customRules: requiredIff({
                                    level: "info",
                                    requiredMessage: "You should name the scenario.",
                                    forbiddenMessage: "Only enter a scenario name if you selected 'Scenario' above.",
                                    otherPath: ["data", "surveyDetails", "surveyType"],
                                    otherTest: value => value === UrsSurveyTypeEnum.Scenario,
                                }),
                            }),
                        ],
                    }),
                    section({
                        title: "1.1 Surveyor and Survey Conditions",
                        path: [],
                        blocks: [
                            timestampField({
                                label: "Survey Date and Time",
                                path: ["data", "recorded"],
                                help: "Date and time the survey was recorded in the field",
                            }),
                            checkbox({
                                label: "Low Tide",
                                path: ["data", "surveyDetails", "lowTide"],
                                checkboxLabel: "Is survey within 2 hours of Low tide today?",
                            }),
                            select({
                                label: "Days Until Spring Tide",
                                path: ["data", "surveyDetails", "daysToSpringTide"],
                                options: enumSelectOptions(MrsMorphEstuariesDaysToSpringTideEnum),
                                required: required("info"),
                                help: outdent `
                If greater than 2 please specify in the notes box.
                `,
                            }),
                            select({
                                label: "Subtidal Zone Visibility",
                                path: ["data", "surveyDetails", "subtidalZoneVisibility"],
                                options: enumSelectOptions(MrsMorphEstuariesVisibilityEnum),
                                required: required("info"),
                            }),
                            select({
                                label: "Surveyed Edge",
                                path: ["data", "surveyDetails", "surveyEdge"],
                                options: enumSelectOptions(UrsSurveyBankEnum),
                                required: required("info"),
                                help: "Which estuary edge is being surveyed?",
                            }),
                            checkbox({
                                label: "Adverse Conditions?",
                                path: ["data", "surveyDetails", "adverseConditions"],
                                checkboxLabel: "Were there adverse conditions at the time of the survey?",
                                defaultValue: false,
                                help: outdent `
                Examples of adverse conditions include: limited access, high river flows,
                impenetrable riparian vegetation, and poor light conditions.
                `,
                            }),
                            textField({
                                label: "Adverse Conditions Notes",
                                path: ["data", "surveyDetails", "adverseConditionsDescription"],
                                help: "If there were adverse conditions, please describe them.",
                                customRules: [
                                    requiredIf({
                                        level: "info",
                                        message: "Please describe the adverse conditions.",
                                        otherPath: ["data", "surveyDetails", "adverseConditions"],
                                        otherTest: value => value === true,
                                    }),
                                ],
                            }),
                        ],
                    }),
                    section({
                        title: "1.2 Module Name and Location",
                        path: [],
                        blocks: [
                            textField({
                                label: "Estuary Name",
                                path: ["data", "surveyDetails", "estuaryName"],
                                required: required("info"),
                                help: "Name of the estuary being surveyed.",
                            }),
                            textField({
                                label: "Reach Name",
                                path: ["data", "surveyDetails", "reachName"],
                                required: required("info"),
                                help: outdent `
                Name of the reach in which the module is located.
                `,
                            }),
                            textField({
                                label: "Subreach Name",
                                path: ["data", "surveyDetails", "subreachName"],
                                required: required("info"),
                                help: outdent `
                Code identifying a set of up to 10 contiguous MoRPhs within the reach.
                `,
                            }),
                            integerField({
                                label: "Module Number",
                                path: ["data", "surveyDetails", "moduleNumber"],
                                required: required("info"),
                                bounds: bounds(1, 10, "error"),
                                help: outdent `
                Module number (1 to 10 from upstream of subreach to downstream).
                `,
                            }),
                        ],
                    }),
                    section({
                        title: "1.3 Channel Dimensions",
                        path: [],
                        blocks: [
                            pointField({
                                label: "Module Location",
                                path: ["data", "surveyDetails", "location"],
                                required: required("info"),
                                fullWidth: true,
                                help: outdent `
                Location of the midpoint of the module.
                `,
                            }),
                            ...[
                                {
                                    label: "Supratidal Terrestrial",
                                    path: "supratidalTerrestrial",
                                    help: outdent `
                  Average width of the area dominated by terrestrial vegetation
                  above and within 10m of the driftline.
                  `,
                                },
                                {
                                    label: "Supratidal Upper Marsh",
                                    path: "supratidalUpper",
                                    help: outdent `
                  Average width of the area dominated by brackish marsh vegetation
                  above and within 10m of the driftline.`,
                                },
                                {
                                    label: "Intertidal Vegetated Middle Marsh",
                                    path: "intertidalVegetatedMiddle",
                                },
                                {
                                    label: "Intertidal Vegetated Lower Marsh",
                                    path: "intertidalVegetatedLower",
                                },
                                {
                                    label: "Intertidal Vegetated Pioneer Marsh",
                                    path: "intertidalVegetatedPioneer",
                                },
                                {
                                    label: "Intertidal Unvegetated Marsh",
                                    path: "intertidalUnvegetated",
                                },
                            ].flatMap(({ path, label, help }) => [
                                checkbox({
                                    label: `${label} Zone Visible?`,
                                    checkboxLabel: "Left Bank",
                                    path: [
                                        "data",
                                        "surveyDetails",
                                        "zones",
                                        path,
                                        "leftBank",
                                        "visible",
                                    ],
                                    help,
                                }),
                                checkbox({
                                    label: `${label} Zone Visible?`,
                                    checkboxLabel: "Right Bank",
                                    path: [
                                        "data",
                                        "surveyDetails",
                                        "zones",
                                        path,
                                        "rightBank",
                                        "visible",
                                    ],
                                    help,
                                }),
                                numberField({
                                    label: `${label} Zone Width`,
                                    secondaryLabel: "Left Bank",
                                    path: [
                                        "data",
                                        "surveyDetails",
                                        "zones",
                                        path,
                                        "leftBank",
                                        "dimension",
                                    ],
                                    units: "m",
                                    customRules: [
                                        forbiddenIf({
                                            level: "info",
                                            message: "You should only specify a value if the zone is visible.",
                                            otherPath: [
                                                "data",
                                                "surveyDetails",
                                                "zones",
                                                path,
                                                "leftBank",
                                                "visible",
                                            ],
                                            otherTest: value => value === true,
                                        }),
                                    ],
                                    help,
                                }),
                                numberField({
                                    label: `${label} Zone Width`,
                                    secondaryLabel: "Right Bank",
                                    path: [
                                        "data",
                                        "surveyDetails",
                                        "zones",
                                        path,
                                        "rightBank",
                                        "dimension",
                                    ],
                                    units: "m",
                                    customRules: [
                                        forbiddenIf({
                                            level: "info",
                                            message: "You should only specify a value if the zone is visible.",
                                            otherPath: [
                                                "data",
                                                "surveyDetails",
                                                "zones",
                                                path,
                                                "rightBank",
                                                "visible",
                                            ],
                                            otherTest: value => value === true,
                                        }),
                                    ],
                                    help,
                                }),
                            ]),
                            checkbox({
                                label: "Subtidal Width at Low Tide",
                                checkboxLabel: "Width visible?",
                                path: [
                                    "data",
                                    "surveyDetails",
                                    "zones",
                                    "waterWidth",
                                    "visible",
                                ],
                                fullWidth: true,
                            }),
                            numberField({
                                label: "Subtidal Width at Low Tide",
                                path: [
                                    "data",
                                    "surveyDetails",
                                    "zones",
                                    "waterWidth",
                                    "dimension",
                                ],
                                fullWidth: true,
                                units: "m",
                                customRules: [
                                    forbiddenIf({
                                        level: "info",
                                        message: "You should only specify a value if the zone is visible.",
                                        otherPath: [
                                            "data",
                                            "surveyDetails",
                                            "zones",
                                            "waterWidth",
                                            "visible",
                                        ],
                                        otherTest: value => value === true,
                                    }),
                                ],
                            }),
                            checkbox({
                                label: "Water Depth",
                                checkboxLabel: "Depth visible?",
                                path: [
                                    "data",
                                    "surveyDetails",
                                    "zones",
                                    "waterDepth",
                                    "visible",
                                ],
                                fullWidth: true,
                                help: "Upper Estuary Only",
                            }),
                            numberField({
                                label: "Water Depth",
                                path: [
                                    "data",
                                    "surveyDetails",
                                    "zones",
                                    "waterDepth",
                                    "dimension",
                                ],
                                fullWidth: true,
                                units: "m",
                                customRules: [
                                    forbiddenIf({
                                        level: "info",
                                        message: "You should only specify a value if the zone is visible.",
                                        otherPath: [
                                            "data",
                                            "surveyDetails",
                                            "zones",
                                            "waterDepth",
                                            "visible",
                                        ],
                                        otherTest: value => value === true,
                                    }),
                                ],
                                help: "Upper Estuary Only",
                            }),
                        ],
                    }),
                    section({
                        title: "1.4 Estuary Bank or Marginal Profile",
                        path: ["data", "surveyDetails", "bankProfile"],
                        blocks: [
                            grid({
                                columns: 2,
                                blocks: [
                                    {
                                        label: "Naturally Variable",
                                        path: "naturallyVariable",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Terraced - Gentle",
                                        path: "terracedGentle",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Resectioned - Sloping",
                                        path: "resectionedSloping",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Terraced - Steep",
                                        path: "terracedSteep",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Resectioned + Bolt-on Timbers",
                                        path: "resectionedBoltOn",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Resectioned - Vertical",
                                        path: "resectionedVertical",
                                        options: enumSelectOptions(MrsAtpeEnum, true),
                                    },
                                    {
                                        label: "Flood Wall",
                                        path: "floodWall",
                                        options: enumSelectOptions(MrsMorphEstuariesFloodWallDimensionEnum, true),
                                        help: outdent `
                  Record when a flood defence structure is present -
                  in meters to the driftline or high water mark
                  `,
                                    },
                                    {
                                        label: "Flood Embankment / Bund",
                                        path: "floodEmbankment",
                                        options: enumSelectOptions(MrsMorphEstuariesFloodWallDimensionEnum, true),
                                        help: outdent `
                  Record when a flood defence structure is present -
                  in meters to the driftline or high water mark
                  `,
                                    },
                                ].flatMap(({ path, label, options, help }) => [
                                    select({
                                        label,
                                        secondaryLabel: "Left Bank",
                                        path: [path, "leftBank"],
                                        options,
                                        help,
                                    }),
                                    select({
                                        label,
                                        secondaryLabel: "Right Bank",
                                        path: [path, "rightBank"],
                                        options,
                                        help,
                                    }),
                                ]),
                            }),
                        ],
                    }),
                    photographsAndNotes(),
                ],
            }),
            page({
                title: "Supratidal Zone / Bank Top",
                path: [],
                blocks: [
                    morphSection({
                        path: ["data", "supratidal", "artificialMaterials"],
                        title: "2.1 Artificial/Managed Ground Cover",
                        blocks: [
                            morphBanks({
                                label: "Dominant Artificial Ground Cover",
                                path: ["dominantGroundCover"],
                                abundance: morphAtpeSelect(),
                                code: morphSelect({
                                    secondaryLabel: "Cover Type",
                                    source: MrsArtificialGroundCoverEnum,
                                }),
                                help: "Within 10m of the bank edge / high water mark",
                            }),
                            morphBanks({
                                label: "Subdominant Artificial Ground Cover",
                                path: ["subdominantGroundCover"],
                                abundance: morphApeSelect(),
                                code: morphSelect({
                                    secondaryLabel: "Cover Type",
                                    source: MrsArtificialGroundCoverEnum,
                                }),
                                help: "Within 10m of the bank edge / high water mark",
                            }),
                        ],
                    }),
                    morphSection({
                        path: ["data", "supratidal", "artificialMaterials"],
                        title: "2.2a Artificial Features",
                        blocks: [
                            morphBanks({
                                label: "Vertical Reinforcement Extent",
                                path: ["reinforcementVerticalExtent"],
                                abundance: morphSelect({
                                    source: MrsReinforcementVerticalExtentEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Horizontal Reinforcement Extent",
                                path: ["reinforcementHorizontalExtent"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Dominant Reinforcement Type",
                                path: ["dominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                            }),
                            morphBanks({
                                label: "Subdominant Reinforcement Type",
                                path: ["subdominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                            }),
                        ],
                    }),
                    morphSection({
                        path: ["data", "supratidal", "artificialFeatures"],
                        title: "2.2b Artificial Habitat Enhancements",
                        blocks: [
                            morphBanks({
                                label: "Terracing - Non Zig Zag",
                                path: ["terraceNonZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Terracing - Zig Zag",
                                path: ["terraceZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Bolt-On Timbers",
                                path: ["boltOnTimbers"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.3a Terrestrial Vegetation",
                        path: [
                            "data",
                            "supratidal",
                            "naturalFeatures",
                            "terrestrialVegetation",
                        ],
                        blocks: [
                            morphBanks({
                                path: ["unvegetated"],
                                label: "Unvegetated (Bare Soil/Rock)",
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Mosses/Lichens",
                                path: ["bryophytes"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/bryophytes.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Short/Creeping Herbs/Grasses",
                                path: ["shortHerbsOrGrasses"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/short-herbs-or-grasses.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Tall Herbs/Grasses (Including Reeds)",
                                path: ["tallHerbsOrGrasses"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/tall-herbs-or-grasses.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Scrub or Shrubs",
                                path: ["scrubOrShrubs"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/scrub-or-shrubs.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Saplings or Trees",
                                path: ["saplingsOrTrees"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/saplings-or-trees.jpg",
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.3b Tree Features",
                        path: ["data", "supratidal", "naturalFeatures", "treeFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Fallen Trees",
                                path: ["fallenTrees"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/fallen-trees.jpg",
                                }),
                                help: "ONLY those with a significant proportion on bank top",
                            }),
                            morphBanks({
                                label: "Leaning Trees",
                                path: ["leaningTrees"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/leaning-trees.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "J-Shaped Trees",
                                path: ["jShapedTrees"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/j-shaped-trees.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Tree/Shrub Branches Trailing Into Channel",
                                path: ["trailingTreeOrShrubBranches"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/trailing-tree-or-shrub-branches.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Large Wood",
                                path: ["largeWood"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "bank-top-terrestrial-vegetation/large-wood.jpg",
                                }),
                                help: "Wood pieces >1m long, >10cm diameter",
                            }),
                            morphBanks({
                                label: "Predominant Tree Type",
                                path: ["predominantTreeType"],
                                code: morphSelect({ source: MrsTreeTypeEnum }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.3c Non-Native Invasive Plant Species",
                        path: [
                            "data",
                            "supratidal",
                            "naturalFeatures",
                            "nuisancePlantSpecies",
                        ],
                        blocks: [
                            morphBanks({
                                label: "Himalayan Balsam",
                                path: ["himalayanBalsam"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "nuisance-plant-species/himalayan-balsam.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Japanese Knotweed",
                                path: ["japaneseKnotweed"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "nuisance-plant-species/japanese-knotweed.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Giant Hogweed",
                                path: ["giantHogweed"],
                                abundance: morphAtpeSelect({
                                    defaultImage: "nuisance-plant-species/giant-hogweed.jpg",
                                }),
                            }),
                            morphBanks({
                                label: "Other Species 1",
                                path: ["otherSpecies1"],
                                abundance: morphAtpeSelect(),
                                code: morphText({ secondaryLabel: "$bank Species" }),
                            }),
                            morphBanks({
                                label: "Other Species 2",
                                path: ["otherSpecies2"],
                                abundance: morphAtpeSelect(),
                                code: morphText({ secondaryLabel: "$bank Species" }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.4a Ponds/Lagoons",
                        path: ["data", "supratidal", "freshwaterFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Disconnected From Estuary at Time of Survey",
                                path: ["disconnectedPond"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Connected to Estuary",
                                path: ["connectedPond"],
                                abundance: morphAtpeSelect(),
                                help: "By water-filled channel at time of survey",
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.4b Side Channels",
                        path: ["data", "supratidal", "freshwaterFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Free Flowing Separate Channel",
                                path: ["freeFlowingSideChannel"],
                                abundance: morphAtpeSelect(),
                                help: "Including tributaries and fish passes",
                            }),
                            morphBanks({
                                label: "Non-Flowing With Control Structure",
                                path: ["nonFlowingSideChannel"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. gate/sluice/tidal flaps etc",
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.4c Wetland",
                        path: ["data", "supratidal", "freshwaterFeatures"],
                        help: "Recorded by dominant vegetation type",
                        blocks: [
                            morphBanks({
                                label: "Short Non-Woody Vegetation",
                                path: ["wetlandShortNonWoody"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. mosses, sedges",
                            }),
                            morphBanks({
                                label: "Tall, Non-Woody Vegetation",
                                path: ["wetlandTallNonWoody"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. reeds, rushes",
                            }),
                            morphBanks({
                                label: "Shrubs and Trees",
                                path: ["wetlandShrubsAndTrees"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. alder / willow carr",
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.5 Brackish / Upper Salt Marsh Zone",
                        path: ["data", "supratidal", "brackishFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Upper Salt Marsh Vegetation Community",
                                path: ["vegetation"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Reedbeds",
                                path: ["reedbeds"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Algae",
                                path: ["algae"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Dry Scrapes",
                                path: ["dryScrapes"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Dunes",
                                path: ["dunes"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "2.6 Litter",
                        path: ["data", "supratidal", "litter"],
                        blocks: [
                            morphBanks({
                                label: "Large Immobile Litter",
                                path: ["largeImmobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. shopping trolleys, tyres, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Mobile/Floating Litter",
                                path: ["floatingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. bottles, polystyrene blocks, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Mobile/Sinking Litter",
                                path: ["sinkingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. plastic bags, wet wipes, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Small Fragments / Visible Particles *",
                                path: ["smallFragments"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. polystyrene balls, etc",
                                }),
                            }),
                            morphBanks({
                                label: "* Fragments or Particles Present as a Continuous Drape or Patchy Deposits?",
                                path: ["smallFragmentDistribution"],
                                abundance: morphSelect({
                                    source: MrsMorphEstuariesSmallFragmentDistributionEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Other Litter",
                                path: ["other"],
                                code: morphText({
                                    secondaryLabel: "$bank Other Litter",
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                ],
            }),
            page({
                title: "Vegetated Intertidal Zone",
                path: [],
                blocks: [
                    morphSection({
                        title: "3.1a Natural Materials",
                        path: ["data", "vegetatedIntertidal", "naturalMaterials"],
                        blocks: [
                            morphBanks({
                                label: "Dominant",
                                path: ["dominant"],
                                abundance: morphAtpeSelect(),
                                code: morphSelect({
                                    source: MrsMorphEstuariesVegetatedIntertidalSedimentSizeEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Subdominant",
                                path: ["subdominant"],
                                abundance: morphApeSelect(),
                                code: morphSelect({
                                    source: MrsMorphEstuariesVegetatedIntertidalSedimentSizeEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.1b Artificial Materials / Reinforcement",
                        path: ["data", "vegetatedIntertidal", "artificialMaterials"],
                        blocks: [
                            morphBanks({
                                label: "Vertical Reinforcement Extent",
                                path: ["reinforcementVerticalExtent"],
                                abundance: morphSelect({
                                    source: MrsReinforcementVerticalExtentEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Horizontal Reinforcement Extent",
                                path: ["reinforcementHorizontalExtent"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Dominant Reinforcement Type",
                                path: ["dominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                            }),
                            morphBanks({
                                label: "Subdominant Reinforcement Type",
                                path: ["subdominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.2a Artificial Habitat Enhancements",
                        path: ["data", "vegetatedIntertidal", "artificialFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Terrace Planform - Non Zig Zag",
                                path: ["terraceNonZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Terrace Planform - Zig Zag",
                                path: ["terraceZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Bolt-On Timbers",
                                path: ["boltOnTimbers"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Deflector / Groyne",
                                path: ["deflectorOrGroyne"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Jetty",
                                path: ["jetty"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Pontoon",
                                path: ["pontoon"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.3a Freshwater Inlets",
                        path: ["data", "vegetatedIntertidal", "freshwaterFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Tributary River/Stream Junctions/Confluences",
                                path: ["confluences"],
                                abundance: morphCount(),
                            }),
                            morphBanks({
                                label: "Pipes/Outfalls",
                                path: ["outfalls"],
                                abundance: morphCount(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.4a Margin Form & Creek Network",
                        path: ["data", "vegetatedIntertidal", "marginAndCreek"],
                        blocks: [
                            morphBanks({
                                label: "Estuary Margin - Dominant Form",
                                path: ["estuaryMargin"],
                                code: morphSelect({ source: MrsMorphEstuariesMarginTypeEnum }),
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Creek Drainage Network",
                                path: ["drainageNetwork"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesDrainageNetworkTypeEnum,
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Creek Type",
                                path: ["creek"],
                                code: morphSelect({ source: MrsMorphEstuariesCreekTypeEnum }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.4b Salt Marsh Fragmentation",
                        path: ["data", "vegetatedIntertidal", "marginAndCreek"],
                        blocks: [
                            morphBanks({
                                label: "Degree of Salt Marsh Fragmentation",
                                path: ["fragmentation"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFragmentationEnum,
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.5a Middle Salt Marsh Vegetation",
                        path: ["data", "vegetatedIntertidal", "middleSaltMarsh"],
                        blocks: [
                            morphBanks({
                                label: "Unvegetated (Bare Earth or Rock)",
                                path: ["unvegetated"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Middle Salt Marsh Vegetation Community",
                                path: ["vegetation"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Algae",
                                path: ["algae"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Reedbeds",
                                path: ["reedbeds"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.5b Invasive Species",
                        path: ["data", "vegetatedIntertidal", "middleSaltMarsh"],
                        blocks: [
                            morphBanks({
                                label: "Cord Grass (Spartina Anglica)",
                                path: ["cordGrass"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Other Invasive Species",
                                path: ["otherInvasive"],
                                code: morphText({ secondaryLabel: "$bank Species" }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.6a Lower and Pioneer Salt Marsh Vegetation",
                        path: ["data", "vegetatedIntertidal", "lowerAndPioneerSaltMarsh"],
                        blocks: [
                            morphBanks({
                                label: "Unvegetated (Bare Earth or Rock)",
                                path: ["unvegetated"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Lower Salt Marsh Vegetation Community",
                                path: ["lowerVegetation"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Algae",
                                path: ["algae"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Pioneer Salt Marsh Vegetation Community",
                                path: ["pioneerVegetation"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.6b Invasive Species",
                        path: ["data", "vegetatedIntertidal", "lowerAndPioneerSaltMarsh"],
                        blocks: [
                            morphBanks({
                                label: "Cord Grass (Spartina Anglica)",
                                path: ["cordGrass"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Other Invasive Species",
                                path: ["otherInvasive"],
                                code: morphText({ secondaryLabel: "$bank Species" }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.7 Intertidal (Vegetated) Zone Natural Features",
                        path: ["data", "vegetatedIntertidal", "naturalFeatures"],
                        blocks: [
                            morphBanks({
                                label: "Salt Pans",
                                path: ["saltPans"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Pockets of Natural Gravels/Sands",
                                path: ["pocketsOfNaturalGravelsOrSands"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Natural Large Wood",
                                path: ["naturalLargeWood"],
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Discrete Organic Accumulation",
                                path: ["discreteOrganicAccumulation"],
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.8a Erosion St Malt Varsh cliff edges or creek margins",
                        path: ["data", "vegetatedIntertidal", "pressures"],
                        blocks: [
                            morphBanks({
                                label: "Rotational Slumps",
                                path: ["rotationalSlumps"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFrontBackCreekMarginEnum,
                                }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Toppling Features",
                                path: ["topplingFeatures"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFrontBackCreekMarginEnum,
                                }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Nest Holes / Burrows",
                                path: ["nestHolesOrBurrows"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFrontBackCreekMarginEnum,
                                }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Stable Cliff (> 0.5m)",
                                path: ["stableCliff"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFrontBackCreekMarginEnum,
                                }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Eroding Sliff (> 0.5m)",
                                path: ["erodingCliff"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesFrontBackCreekMarginEnum,
                                }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.8b Pressures on Salt Marshes",
                        path: ["data", "vegetatedIntertidal", "pressures"],
                        blocks: [
                            morphBanks({
                                label: "Grazing",
                                path: ["grazing"],
                                code: morphSelect({ source: MrsMorphEstuariesGrazingEnum }),
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Trampling/Poaching/Crushing",
                                path: ["tramplingOrPoachingOrCrushing"],
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Scour Around Vessels",
                                path: ["scourAroundVessels"],
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Scour Around Artificial Structures/Pillars",
                                path: ["scourAroundStructures"],
                                abundance: morphAtpeSelect(),
                                location: morphSelect({
                                    source: MrsMorphEstuariesSaltMarshLocationEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "3.9 Litter",
                        path: ["data", "vegetatedIntertidal", "litter"],
                        blocks: [
                            morphBanks({
                                label: "Large Immobile Litter",
                                path: ["largeImmobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. shopping trolleys, tyres, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Mobile/Floating Litter",
                                path: ["floatingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. bottles, polystyrene blocks, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Mobile/Sinking Litter",
                                path: ["sinkingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. plastic bags, wet wipes, etc",
                                }),
                            }),
                            morphBanks({
                                label: "Small Fragments / Visible Particles *",
                                path: ["smallFragments"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. polystyrene balls, etc",
                                }),
                            }),
                            morphBanks({
                                label: "* Fragments or Particles Present as a Continuous Drape or Patchy Deposits?",
                                path: ["smallFragmentDistribution"],
                                abundance: morphSelect({
                                    source: MrsMorphEstuariesSmallFragmentDistributionEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Other Litter",
                                path: ["other"],
                                code: morphText({
                                    secondaryLabel: "$bank Other Litter",
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                ],
            }),
            page({
                title: "Unvegetated Intertidal Zone",
                path: [],
                blocks: [
                    morphSection({
                        title: "4.1a Natural Materials",
                        path: ["data", "unvegetatedIntertidal", "naturalMaterials"],
                        blocks: [
                            morphBanks({
                                label: "Bedrock",
                                path: ["bedrock"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Boulder",
                                path: ["boulder"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Cobble",
                                path: ["cobble"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Gravel/Pebble",
                                path: ["gravelPebble"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Shelly Material",
                                path: ["shellyMaterial"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Sand",
                                path: ["sand"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Fine Sediments",
                                path: ["fineSediments"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. silt & clay",
                            }),
                            morphBanks({
                                label: "Fluid Mud",
                                path: ["fluidMud"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Organic",
                                path: ["organic"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.1b Artificial Materials",
                        path: ["data", "unvegetatedIntertidal", "artificialMaterials"],
                        blocks: [
                            morphBanks({
                                label: "Vertical Reinforcement Extent",
                                path: ["reinforcementVerticalExtent"],
                                abundance: morphSelect({
                                    source: MrsReinforcementVerticalExtentEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Horizontal Reinforcement Extent",
                                path: ["reinforcementHorizontalExtent"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Dominant Reinforcement Type",
                                path: ["dominantReinforcement"],
                                code: morphSelect({
                                    source: MrsReinforcementTypeEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Subdominant Reinforcement Type",
                                path: ["subdominantReinforcement"],
                                code: morphSelect({
                                    source: MrsReinforcementTypeEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.2a Habitat Features",
                        path: ["data", "unvegetatedIntertidal", "features"],
                        blocks: [
                            morphBanks({
                                label: "Biogenic Reef",
                                path: ["biogenicReef"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Natural Large Wood",
                                path: ["naturalLargeWood"],
                                abundance: morphAtpeSelect(),
                                help: "Pieces >1m long, >10cm diameter",
                            }),
                            morphBanks({
                                label: "Discrete Organic Accumulation",
                                path: ["discreteOrganicAccumulation"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. leaves, twigs",
                            }),
                            morphBanks({
                                label: "Sea Grass",
                                path: ["seaGrass"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Macro Aglae (Sea Weeds)",
                                path: ["macroAlgae"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Micro Algae / Biofilm",
                                path: ["microAlgae"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.2b Habitat Enhancements",
                        path: ["data", "unvegetatedIntertidal", "features"],
                        blocks: [
                            morphBanks({
                                label: "Terraces - Non Zig Zag",
                                path: ["terraceNonZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Terraces - Zig Zag",
                                path: ["terraceZigZag"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Artificial Wood / Fixed Timbers",
                                path: ["artificialWood"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.3a Artificial Structures",
                        path: ["data", "unvegetatedIntertidal", "pressures"],
                        blocks: [
                            morphBanks({
                                label: "Jetty",
                                path: ["jetty"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Pontoon",
                                path: ["pontoon"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphBanks({
                                label: "Boat Mooring",
                                path: ["boatMooring"],
                                abundance: morphCount(),
                            }),
                            morphBanks({
                                label: "Other Structure",
                                path: ["otherStructure"],
                                code: morphText(),
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.3b Compaction or Erosion",
                        path: ["data", "unvegetatedIntertidal", "pressures"],
                        blocks: [
                            morphBanks({
                                label: "Compaction by Vehicles/Livestock/People",
                                path: ["compaction"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Intertidal Zone Excavation",
                                path: ["excavation"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. channels cut or other earthworks",
                            }),
                            morphBanks({
                                label: "Localised Patches of Scour/Erosion Around Artificial Structures",
                                path: ["scourAroundStructures"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Localised Patches of Scour/Erosion Around or Due to Vessels",
                                path: ["scourAroundVessels"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphBanks({
                                label: "Marginal Backwater",
                                path: ["marginalBackwater"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.3c Wave wash",
                        path: ["data", "unvegetatedIntertidal", "pressures"],
                        blocks: [
                            morphBanks({
                                label: "Visible Wave Wash / Wakes Appearing From Boats Passing During Survey",
                                path: ["waveWash"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.4 Litter",
                        path: ["data", "unvegetatedIntertidal", "litter"],
                        blocks: [
                            morphBanks({
                                label: "Large Immobile Litter",
                                path: ["largeImmobile"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. shopping trolleys, tyres, etc",
                            }),
                            morphBanks({
                                label: "Mobile/Floating Litter",
                                path: ["floatingMobile"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. bottles, polystyrene blocks, etc",
                            }),
                            morphBanks({
                                label: "Mobile/Sinking Litter",
                                path: ["sinkingMobile"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. plastic bags, wet wipes, etc",
                            }),
                            morphBanks({
                                label: "Small Fragments / Visible Particles",
                                path: ["smallFragments"],
                                abundance: morphAtpeSelect(),
                                help: "e.g. polystyrene balls, etc",
                            }),
                            morphBanks({
                                label: "Other Litter",
                                path: ["other"],
                                code: morphText({
                                    secondaryLabel: "$bank Other Litter",
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                ],
            }),
            page({
                title: "Subtidal Zone / Channel Bed",
                path: [],
                blocks: [
                    morphSection({
                        title: "4.5a Channel Bed Sediments",
                        path: ["data", "subtidal", "naturalMaterials"],
                        blocks: [
                            morphChannel({
                                label: "Dominant",
                                path: ["dominant"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesSubtidalSedimentSizeEnum,
                                }),
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Subdominant",
                                path: ["subdominant"],
                                code: morphSelect({
                                    source: MrsMorphEstuariesSubtidalSedimentSizeEnum,
                                }),
                                abundance: morphApeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.5b Artificial materials",
                        path: ["data", "subtidal", "artificialMaterials"],
                        blocks: [
                            morphChannel({
                                label: "Dominant Reinforcement Type",
                                path: ["dominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Subdominant Reinforcement Type",
                                path: ["subdominantReinforcement"],
                                code: morphSelect({ source: MrsReinforcementTypeEnum }),
                                abundance: morphApeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.6a Natural Physical Features",
                        path: ["data", "subtidal", "naturalFeatures"],
                        blocks: [
                            morphChannel({
                                label: "Bedrock",
                                path: ["bedrock"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Boulder (Unvegetated)",
                                path: ["unvegetatedBoulder"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Boulder (Vegetated)",
                                path: ["vegetatedBoulder"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Island",
                                path: ["island"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Mid-Channel Bar (Unvegetated)",
                                path: ["unvegetatedMidChannelBar"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Mid-Channel Bar (Vegetated)",
                                path: ["vegetatedMidChannelBar"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Riffle",
                                path: ["riffle"],
                                abundance: morphCount(),
                            }),
                            morphChannel({
                                label: "Pool",
                                path: ["pool"],
                                abundance: morphCount(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.6b Vegetation Within Wetted Channel",
                        path: ["data", "subtidal", "naturalFeatures"],
                        blocks: [
                            morphChannel({
                                label: "Emergent Broad Leaf",
                                path: ["emergentBroadLeaf"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Emergent Linear",
                                path: ["emergentLinear"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Floating Leaved",
                                path: ["floatingLeaved"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Submerged Broad Leaf",
                                path: ["submergedBroadLeaf"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Submerged Linear/Fine",
                                path: ["submergedLinear"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Filamentous Algae / Channel Choked",
                                path: ["filamentousAlgae"],
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.6c Vegetation interacting with wetted channel",
                        path: ["data", "subtidal", "naturalFeatures"],
                        blocks: [
                            morphChannel({
                                label: "Vegetation Shading Channel",
                                path: ["vegetationShadingChannel"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Submerged Tree Roots",
                                path: ["submergedTreeRoots"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Large Wood in Channel",
                                path: ["largeWoodInChannel"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Discrete Organic Accumulations",
                                path: ["discreteOrganicAccumulations"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Large Wood Dam",
                                path: ["largeWoodDam"],
                                abundance: morphCount(),
                                help: "In and crossing the entire channel",
                            }),
                            morphChannel({
                                label: "Fallen Trees",
                                path: ["fallenTrees"],
                                abundance: morphCount(),
                                help: "Only those with a significant portion in the channel",
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.7 Artificial Structures",
                        path: ["data", "subtidal", "pressures"],
                        blocks: [
                            morphChannel({
                                label: "Tidal Flood Barrier",
                                path: ["tidalFloodBarrier"],
                                abundance: morphSelect({
                                    source: MrsMorphEstuariesTidalFloodBarrierEnum,
                                }),
                            }),
                            morphChannel({
                                label: "Weir",
                                path: ["weir"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphChannel({
                                label: "Jetty",
                                path: ["jetty"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphChannel({
                                label: "Pontoon",
                                path: ["pontoon"],
                                abundance: morphSelect({
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphChannel({
                                label: "Boat Mooring",
                                path: ["boatMooring"],
                                abundance: morphCount(),
                            }),
                            morphChannel({
                                label: "Bridge Pillars",
                                path: ["bridgePillars"],
                                abundance: morphCount(),
                            }),
                            morphChannel({
                                label: "Bridge Shadow",
                                path: ["bridgeShadow"],
                                abundance: morphSelect({
                                    secondaryLabel: "Size",
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                            morphChannel({
                                label: "Shading from Tower Blocks",
                                path: ["shadingFromTowerBlocks"],
                                abundance: morphAtpeSelect(),
                            }),
                            morphChannel({
                                label: "Other Structure",
                                path: ["otherStructure"],
                                code: morphText({
                                    secondaryLabel: "Structure",
                                }),
                                abundance: morphSelect({
                                    secondaryLabel: "Size",
                                    source: MrsMajorIntermediateMinorEnum,
                                }),
                            }),
                        ],
                    }),
                    morphSection({
                        title: "4.8 Subtidal - Litter",
                        path: ["data", "subtidal", "litter"],
                        blocks: [
                            morphChannel({
                                label: "Large Immobile Litter",
                                path: ["largeImmobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. shopping trolleys, tyres, etc",
                                }),
                            }),
                            morphChannel({
                                label: "Mobile/Floating Litter",
                                path: ["floatingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. bottles, polystyrene blocks, etc",
                                }),
                            }),
                            morphChannel({
                                label: "Mobile/Sinking Litter",
                                path: ["sinkingMobile"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. plastic bags, wet wipes, etc",
                                }),
                            }),
                            morphChannel({
                                label: "Small Fragments / Visible Particles *",
                                path: ["smallFragments"],
                                abundance: morphAtpeSelect({
                                    help: "e.g. polystyrene balls, etc",
                                }),
                            }),
                            morphChannel({
                                label: "Other Litter",
                                path: ["other"],
                                code: morphText(),
                                abundance: morphAtpeSelect(),
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });
}
export default mrsMorphEstuariesForm("MoRPh Estuaries");
//# sourceMappingURL=form.js.map